/**
 * "Send for review" — the internal branch.
 *
 * Moved here from `pages/Collection/components/NotifyCollectionModal.tsx`
 * rather than copied: two forms posting to `/notifications/send` would drift,
 * and the collection page now routes through the same wizard. What changed in
 * the move:
 *
 *   * Reviewers come first and the asset filter last. The old order asked for
 *     a subject line before the user had said who they were even talking to.
 *   * The ask ("what should they do?") and a deadline are new — the two
 *     fields `conf/10` added, which until now nothing could set.
 *   * The title says review, not notify.
 *
 * The recipient allow-list, the private-collection group-access flip and the
 * schema/field filtering are unchanged from the original.
 */
import React, { useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useAuthUser } from 'react-auth-kit';
import { useDebounce } from 'use-debounce';

import { useAlerts } from 'src/common/AlertManager';
import { Button, Input, Select, Textarea } from 'src/components';
import { X } from 'src/common/icons';
import { useAccessToken } from 'src/hooks/useAccessToken';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import useModal from 'src/hooks/useModal';
import { sendNotification } from 'src/api/functions/notificationsApi';
import { CollectionSearchQuery } from 'src/api/functions/collectionsApi';
import { useCollectionSearchQuery } from 'src/api/queries/collectionsQueries';
import { useAssetSchemasListQuery } from 'src/api/queries/schemesQueries';
import { useGroupMembersQuery } from 'src/api/queries/userQueries';
import { useCollectionMutation } from 'src/api/mutations/collectionMutations';
import { queryNames } from 'src/api/utils/queryNames';
import { TTaskIntent } from 'src/types/TNotification';
import { AssetSchema, AssetSchemaProperty } from 'src/types/TSchemes';

import { INTENT_LABEL } from './reviewRows';
import {
  FieldRow,
  isSelectableSchema,
  pickDefaultSchema,
  seedRowsFor,
} from './reviewSchemaScope';
import styles from './styles.module.scss';

type Recipient = { email: string; name: string };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INTENT_HINT: Record<TTaskIntent, string> = {
  approve: 'They give a yes or no, and you are waiting on it.',
  comment: 'You want their input, but nothing is blocked on it.',
  fix: 'You expect new files back, not an opinion.',
};

const INTENT_ORDER: TTaskIntent[] = ['approve', 'comment', 'fix'];

/** `<input type="date">` wants YYYY-MM-DD, and so does the API — a bare date
 *  is read as end of that day in UTC, so "due Friday" is not overdue at 00:01
 *  on Friday. No conversion needed in either direction. */
const todayISO = () => new Date().toISOString().slice(0, 10);

function schemaLabel(schema: AssetSchema): string {
  return schema.name?.en || schema.schema?.title || `Schema #${schema.id}`;
}

function fieldLabel(key: string, property?: AssetSchemaProperty): string {
  return property?.filespin_properties?.title?.en || property?.title || key;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export type ReviewInternalStepProps = {
  collectionName: string;
  collectionId: number;
  /** Whether the collection is already shared with the group (`public`). */
  groupAccess: boolean;
  /** Seeds the filter state from the page's current filtered view. */
  initialQuery?: CollectionSearchQuery;
  onBack?: () => void;
};

export const ReviewInternalStep: React.FC<ReviewInternalStepProps> = ({
  collectionName,
  collectionId,
  groupAccess,
  initialQuery,
  onBack,
}) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const { showSuccess, showAlert } = useAlerts();
  const { getErrorMessage } = useErrorMessages();
  const { closeModal } = useModal();

  const auth = useAuthUser();
  const currentUserEmail = (auth() ? auth()!.userEmail : '')?.toLowerCase() ?? '';

  // --- reviewers (group-directory lookahead + manual email entry) ---
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [recipientInput, setRecipientInput] = useState('');
  const [inputFocused, setInputFocused] = useState(false);
  const [debouncedTerm] = useDebounce(recipientInput.trim(), 250);

  const membersQuery = useGroupMembersQuery(debouncedTerm);
  const memberOptions = useMemo(() => {
    const members = membersQuery.data?.data?.members ?? [];
    return members.filter(
      (m) =>
        m.email.toLowerCase() !== currentUserEmail &&
        !recipients.some((r) => r.email.toLowerCase() === m.email.toLowerCase()),
    );
  }, [membersQuery.data, recipients, currentUserEmail]);

  const showDropdown = inputFocused && debouncedTerm.length >= 2 && memberOptions.length > 0;

  const addRecipient = (email: string, name?: string): void => {
    const normalized = email.trim().toLowerCase();
    if (!normalized || !EMAIL_RE.test(normalized) || normalized === currentUserEmail) {
      return;
    }
    setRecipientInput('');
    if (recipients.some((r) => r.email.toLowerCase() === normalized)) {
      return;
    }
    setRecipients((prev) => [...prev, { email: normalized, name: name?.trim() || normalized }]);
  };

  const removeRecipient = (email: string) =>
    setRecipients((prev) => prev.filter((r) => r.email !== email));

  const handleRecipientChange = (value: string) => {
    // Allow comma/semicolon to commit a manually typed email.
    if (/[,;]$/.test(value)) {
      addRecipient(value.replace(/[,;]+$/, ''));
    } else {
      setRecipientInput(value);
    }
  };

  // --- the ask ---
  const [intent, setIntent] = useState<TTaskIntent>('approve');
  const [dueOn, setDueOn] = useState('');

  const [subject, setSubject] = useState(`Review: ${collectionName}`);
  const [messageOverride, setMessageOverride] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);

  // --- filters (select schema -> add field -> set value) ---
  const { data: schemasResult } = useAssetSchemasListQuery('ACTIVE');
  const schemas: AssetSchema[] = schemasResult?.data ?? [];

  const [schemaId, setSchemaId] = useState<string>(() => {
    const id = initialQuery?.schema_criteria?.data_schema_id;
    return id != null ? String(id) : '';
  });
  const [fieldRows, setFieldRows] = useState<FieldRow[]>(() => {
    const seeded = initialQuery?.schema_criteria?.fields;
    if (seeded && Object.keys(seeded).length) {
      return Object.entries(seeded).map(([field, value]) => ({ field, value: String(value) }));
    }
    return [];
  });

  // "Everything" vs "by asset data" is now the question, and the schema is a
  // follow-up rather than the question itself. The old control put every
  // schema name straight into "Which assets?", which asked the requester to
  // infer that naming a schema meant "assets described by it" — and buried
  // the actual capability (filter on a field like status) one interaction
  // further in, behind a control that only appeared once they had guessed.
  const [byAssetData, setByAssetData] = useState<boolean>(
    () => initialQuery?.schema_criteria?.data_schema_id != null,
  );

  // Only meaningful once the list has loaded; `undefined` is what hides the
  // "By asset data" option.
  const defaultSchema = useMemo(() => pickDefaultSchema(schemas), [schemas]);
  const selectableSchemas = useMemo(() => schemas.filter(isSelectableSchema), [schemas]);

  const selectedSchema = useMemo(
    () => schemas.find((s) => String(s.id) === schemaId),
    [schemas, schemaId],
  );
  const properties = selectedSchema?.schema?.properties ?? {};
  const hasProperties = Object.keys(properties).length > 0;

  const fields = useMemo(() => {
    const out: Record<string, string> = {};
    fieldRows.forEach(({ field, value }) => {
      if (field && value) out[field] = value;
    });
    return out;
  }, [fieldRows]);

  const dataSchemaId = schemaId ? parseInt(schemaId, 10) : undefined;
  const hasFields = Object.keys(fields).length > 0;

  const modalQuery = useMemo<CollectionSearchQuery>(() => {
    const q: CollectionSearchQuery = { limit: 1, offset: 0 };
    if ((dataSchemaId !== undefined && !Number.isNaN(dataSchemaId)) || hasFields) {
      q.schema_criteria = {
        ...(dataSchemaId !== undefined && !Number.isNaN(dataSchemaId)
          ? { data_schema_id: dataSchemaId }
          : {}),
        ...(hasFields ? { fields } : {}),
      };
    }
    return q;
  }, [dataSchemaId, hasFields, fields]);

  const { data: searchData, isFetching: isResolvingLink } = useCollectionSearchQuery(
    collectionId,
    modalQuery,
  );
  const viewUrl = searchData?.data?.view_url ?? '';
  const total = searchData?.data?.total ?? 0;

  const handleSchemaChange = (value: string) => {
    setSchemaId(value);
    // Field keys belong to a schema, so the rows cannot survive a schema
    // change — reseed them (status again, where the new schema has one)
    // rather than clearing to nothing and making the requester start over.
    setFieldRows(seedRowsFor(schemas.find((s) => String(s.id) === value)));
  };

  /** The "Which assets?" control: a scope, not a schema. */
  const handleScopeChange = (value: string) => {
    if (value === 'data') {
      if (!defaultSchema) return;
      setByAssetData(true);
      // Only pick a schema if the requester has not already chosen one —
      // switching to Everything and back should not silently move them off it.
      const target = schemaId
        ? schemas.find((s) => String(s.id) === schemaId) ?? defaultSchema
        : defaultSchema;
      setSchemaId(String(target.id));
      setFieldRows((rows) => (rows.length ? rows : seedRowsFor(target)));
      return;
    }
    setByAssetData(false);
    // Everything means everything: leave no schema or field behind to keep
    // narrowing `modalQuery` from under the requester.
    setSchemaId('');
    setFieldRows([]);
  };

  const addFieldRow = () => setFieldRows((prev) => [...prev, { field: '', value: '' }]);
  const updateFieldRow = (index: number, patch: Partial<FieldRow>) =>
    setFieldRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const removeFieldRow = (index: number) =>
    setFieldRows((prev) => prev.filter((_, i) => i !== index));

  const defaultMessage = useMemo(
    () =>
      `I've shared ${total} asset${total === 1 ? '' : 's'} from the "${collectionName}" collection with you.`,
    [total, collectionName],
  );
  const message = messageOverride ?? defaultMessage;

  const patchCollectionMutation = useCollectionMutation(collectionId);

  const isValid = useMemo(
    () => recipients.length > 0 && subject.trim().length > 0 && !!viewUrl,
    [recipients, subject, viewUrl],
  );

  const handleSend = async () => {
    if (!isValid || isSending) return;

    setIsSending(true);
    try {
      // A private collection must be shared with the group so the reviewer can
      // open the deep link. The owner is the only viewer of a private
      // collection, so this flip always has permission.
      if (!groupAccess) {
        await patchCollectionMutation.mutateAsync({ group_access: true });
        queryClient.invalidateQueries([queryNames.collection, collectionId]);
      }

      const bodyText = `${message.trim()}\n\nView the assets here: ${viewUrl}`;
      const bodyHtml = `<p>${escapeHtml(message.trim())}</p><p><a href="${viewUrl}">View the assets</a></p>`;

      const result: any = await sendNotification(
        {
          to: recipients.map((r) => r.email),
          subject: subject.trim(),
          body: bodyText,
          body_html: bodyHtml,
          url: viewUrl,
          intent,
          due_on: dueOn || undefined,
        },
        accessToken,
      );

      const rejected: string[] = result?.data?.rejected_recipients ?? [];
      if (rejected.length) {
        showAlert(`Some reviewers were not allowed: ${rejected.join(', ')}`);
      } else {
        showSuccess(
          `Review sent to ${recipients.length} ${recipients.length === 1 ? 'person' : 'people'}`,
        );
      }
      // Both halves of the loop change: the reviewer's queue and the
      // requester's list.
      queryClient.invalidateQueries([queryNames.notification]);
      closeModal();
    } catch (error: any) {
      showAlert(getErrorMessage(error?.message));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <div className={styles.wizStep}>
        <div className={styles.field}>
          {/* Explicit label rather than the Input's own: the chosen reviewers
              render as chips between the label and the search box, and an
              Input-owned label would sit above them. Input takes no `id`, so
              the search box carries its own aria-label instead of htmlFor. */}
          <label>Reviewers</label>
          {recipients.length > 0 && (
            <div className={styles.recipientChips}>
              {recipients.map((r) => (
                <span key={r.email} className={styles.recipientChip} title={r.email}>
                  {r.name}
                  <button
                    type="button"
                    aria-label={`Remove ${r.email}`}
                    onClick={() => removeRecipient(r.email)}
                  >
                    <X />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className={styles.recipientField}>
            <Input
              placeholder="Search teammates by name or email..."
              value={recipientInput}
              onChange={handleRecipientChange}
              onEnter={(value) => addRecipient(value)}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setTimeout(() => setInputFocused(false), 150)}
            />
            {showDropdown && (
              <ul className={styles.recipientDropdown}>
                {memberOptions.map((m) => {
                  const name = `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() || m.email;
                  return (
                    <li
                      key={m.id}
                      className={styles.recipientOption}
                      // onMouseDown fires before the input's blur, so the click registers.
                      onMouseDown={(e) => {
                        e.preventDefault();
                        addRecipient(m.email, name);
                      }}
                    >
                      <span className={styles.recipientOptionName}>{name}</span>
                      <span className={styles.recipientOptionEmail}>{m.email}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <span className={styles.fieldHint}>
            Teammates only. Each reviewer gets their own task, so one can finish while
            another is still looking.
          </span>
        </div>

        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <Select
              label="What should they do?"
              value={intent}
              onChange={(value: string) => setIntent(value as TTaskIntent)}
              options={INTENT_ORDER.map((v) => ({ value: v, label: INTENT_LABEL[v] }))}
            />
            <span className={styles.fieldHint}>{INTENT_HINT[intent]}</span>
          </div>

          <div className={styles.field}>
            <label htmlFor="review-due">Due (optional)</label>
            <input
              id="review-due"
              type="date"
              className={styles.dateInput}
              value={dueOn}
              min={todayISO()}
              onChange={(e) => setDueOn(e.target.value)}
            />
            <span className={styles.fieldHint}>
              {dueOn ? 'Counts as end of that day.' : 'No deadline — it will never read as overdue.'}
            </span>
          </div>
        </div>

        <div className={styles.field}>
          <Input
            label="Subject"
            placeholder="Subject"
            value={subject}
            onChange={(value) => setSubject(value)}
          />
        </div>

        <div className={styles.field}>
          <Textarea
            label="Message"
            placeholder="Add a short message..."
            rows={3}
            value={message}
            onChange={(value) => setMessageOverride(value)}
          />
        </div>

        <div className={styles.field}>
          <Select
            label="Which assets?"
            value={byAssetData ? 'data' : ''}
            onChange={handleScopeChange}
            options={[
              { value: '', label: 'Everything in this collection' },
              // Hidden outright when the account has no schema to filter on —
              // offering it would open a schema picker with nothing in it.
              ...(defaultSchema
                ? [{ value: 'data', label: 'By asset data (such as status)' }]
                : []),
            ]}
          />
        </div>

        {byAssetData && (
          <div className={styles.field}>
            <Select
              label="Asset schema"
              value={schemaId}
              onChange={handleSchemaChange}
              options={selectableSchemas.map((s) => ({
                value: String(s.id),
                label: schemaLabel(s),
              }))}
            />
            <span className={styles.fieldHint}>
              Only assets described by this schema are included.
            </span>
          </div>
        )}

        {byAssetData && schemaId && hasProperties && (
          <div className={styles.field}>
            {fieldRows.map((row, index) => {
              const property = row.field ? properties[row.field] : undefined;
              const enumValues = property?.enum;
              return (
                <div key={index} className={styles.filterRow}>
                  <Select
                    value={row.field}
                    onChange={(value) => updateFieldRow(index, { field: value, value: '' })}
                    options={[
                      { value: '', label: 'Select field' },
                      ...Object.entries(properties).map(([key, prop]) => ({
                        value: key,
                        label: fieldLabel(key, prop),
                      })),
                    ]}
                  />
                  {enumValues && enumValues.length ? (
                    <Select
                      value={row.value}
                      onChange={(value) => updateFieldRow(index, { value })}
                      options={[
                        { value: '', label: 'Select value' },
                        ...enumValues.map((v) => ({ value: v, label: v })),
                      ]}
                    />
                  ) : (
                    <Input
                      placeholder="Value (e.g. approved)"
                      value={row.value}
                      onChange={(value) => updateFieldRow(index, { value })}
                    />
                  )}
                  <button
                    type="button"
                    className={styles.filterRowRemove}
                    aria-label="Remove filter"
                    onClick={() => removeFieldRow(index)}
                  >
                    <X />
                  </button>
                </div>
              );
            })}

            <Button size="small" mode="tertiary" onClick={addFieldRow}>
              Add data filter
            </Button>
          </div>
        )}

        {!groupAccess && (
          <p className={styles.wizNote}>
            This collection is private. Sending it will share it with your group so reviewers
            can open the link.
          </p>
        )}

        <p className={styles.wizNote}>
          {isResolvingLink ? (
            'Preparing link...'
          ) : viewUrl ? (
            <>
              {total} asset{total === 1 ? '' : 's'} match. Reviewers open:{' '}
              <a href={viewUrl} target="_blank" rel="noopener noreferrer">
                {viewUrl}
              </a>
            </>
          ) : (
            'Link not available yet.'
          )}
        </p>
      </div>

      <div className={styles.wizActions}>
        {onBack && (
          <Button size="small" mode="tertiary" onClick={onBack}>
            Back
          </Button>
        )}
        <Button size="small" mode="tertiary" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          size="small"
          mode="primary"
          onClick={handleSend}
          disabled={!isValid}
          loading={isSending}
        >
          {recipients.length > 1
            ? `Send ${recipients.length} review tasks`
            : 'Send review task'}
        </Button>
      </div>
    </>
  );
};

export default ReviewInternalStep;
